import { supabase, type Movimiento } from '../lib/supabase';
import { fechaInicioMes, fechaFinMes } from '../utils/dateHelpers';
import { calcularTotales, esFinanciamiento, esIngresoPropio, filtrarQueAfectanBalance, type MovimientoMonto } from '../utils/balanceCalculations';
import type { OwnerFondoCampana, SaldoRestringidoCampana } from '../types/fondosCampana';
import { validarSaldosRestringidosCampana } from '../utils/fondosCampanaRpc';
import { SELECT_MOVIMIENTO_CON_COMPROBANTES } from './movimientoService';

function getPeriodoFechas(periodo: PeriodoBalance): { fechaInicio: string; fechaFin: string } {
  if (periodo.tipo === 'mensual') {
    if (!periodo.mes || periodo.mes < 1 || periodo.mes > 12) {
      throw new Error('Periodo mensual requiere un mes válido (1-12).');
    }
    return {
      fechaInicio: fechaInicioMes(periodo.mes, periodo.año),
      fechaFin: fechaFinMes(periodo.mes, periodo.año),
    };
  }
  return {
    fechaInicio: `${periodo.año}-01-01`,
    fechaFin: `${periodo.año}-12-31`,
  };
}

export interface BalanceResumen {
  ingresos: number;
  egresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance. */
  financiamiento: number;
  balance: number;
  /**
   * Movimientos no anulados del período, ingresos y egresos juntos. Cuenta también los que NO
   * afectan balance (cuota iglesia, donaciones), a diferencia de las cifras de dinero de arriba:
   * su propósito es anticipar cuántas filas verá el usuario en la pantalla de Movimientos, que en
   * su estado por defecto ("Mostrar anulados" apagado) muestra exactamente este conjunto.
   * No "corregir" esto para que cuadre con `ingresos`/`egresos`: son preguntas distintas.
   */
  cantidadMovimientos: number;
  ingresosDesglosados: {
    aportesMensualesMiembros: number;
    cuotaIglesiaRecaudada: number;
    cuotaIglesiaJal: number;
    cuotaIglesiaPA: number;
    aportesExternos: number;
    actividadesEconomicas: number;
    campanas: number;
  };
  egresosDesglosados: {
    actividades: number;
    gastosGenerales: number;
    gastosLiturgicos: number;
    gastosMisionales: number;
    otros: number;
  };
}

export interface PeriodoBalance {
  tipo: 'mensual' | 'anual';
  mes?: number;
  año: number;
}

export interface EvolucionMensualBalance {
  readonly mes: number;
  readonly ingresos: number;
  readonly egresos: number;
  readonly financiamiento: number;
  readonly balance: number;
}

export const balanceService = {
  /** Saldo actual: no representa el cierre histórico del período seleccionado. */
  async getSaldosRestringidosCampana(owner: OwnerFondoCampana): Promise<SaldoRestringidoCampana[]> {
    const { data, error } = await supabase.rpc('saldos_restringidos_campana', {
      p_tipo_entidad: owner.tipo, p_entidad_id: owner.id,
    });
    if (error) throw error;
    return validarSaldosRestringidosCampana(data, owner);
  },

  async getMovimientosFondosCampana(owner: OwnerFondoCampana, periodo: PeriodoBalance): Promise<Movimiento[]> {
    const { fechaInicio, fechaFin } = getPeriodoFechas(periodo);
    const columna = { lumisial: 'lumisial_id', diocesis: 'diocesis_id', provincia: 'provincia_eclesiastica_id' }[owner.tipo];
    const movimientos: Movimiento[] = [];
    for (let desde = 0; ; desde += 500) {
      const { data, error } = await supabase.from('movimientos').select(SELECT_MOVIMIENTO_CON_COMPROBANTES)
        .eq(columna, owner.id).eq('origen_tipo', 'campaña').eq('afecta_balance', false).eq('anulado', false)
        .gte('fecha', fechaInicio).lte('fecha', fechaFin)
        .order('fecha', { ascending: false }).order('id').range(desde, desde + 499);
      if (error) throw error;
      // La constante del select no es literal, así que Supabase no puede inferir el tipo de la fila.
      movimientos.push(...(data ?? []) as unknown as Movimiento[]);
      if (!data || data.length < 500) return movimientos;
    }
  },

  async getBalanceByPeriodo(lumisialId: string, periodo: PeriodoBalance): Promise<BalanceResumen> {
    const { fechaInicio, fechaFin } = getPeriodoFechas(periodo);

    const { data: movimientos, error } = await supabase
      .from('movimientos')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) throw error;

    // `ingresos` queda SIN filtrar a propósito: los desgloses de ingreso deben mostrar también el
    // dinero en tránsito (cuotaIglesiaRecaudada existe justamente para eso). El filtro se aplica al
    // total, más abajo. En egresos sí se filtra el arreglo completo, porque sus seis desgloses y su
    // total deben derivar todos del mismo conjunto.
    const ingresos = movimientos?.filter(m => m.tipo === 'ingreso') || [];
    const egresos = filtrarQueAfectanBalance(movimientos?.filter(m => m.tipo === 'egreso') || []);

    // El financiamiento de una deuda es dinero prestado: no es un ingreso propio, pero sí suma al balance.
    const ingresosQueAfectan = filtrarQueAfectanBalance(ingresos);
    const totalIngresosConBalance = ingresosQueAfectan.filter(esIngresoPropio)
      .reduce((sum, m) => sum + Number(m.monto), 0);
    const financiamiento = ingresosQueAfectan.filter(esFinanciamiento)
      .reduce((sum, m) => sum + Number(m.monto), 0);
    const totalEgresos = egresos.reduce((sum, m) => sum + Number(m.monto), 0);

    const aportesMensualesMiembros = ingresos
      .filter(m => m.origen_tipo === 'miembro' && m.categoria_aporte === 'aporte_mensual')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const movsCuota = ingresos.filter(m => m.origen_tipo === 'miembro' && m.categoria_aporte === 'cuota_iglesia');
    const cuotaIglesiaRecaudada = movsCuota.reduce((sum, m) => sum + Number(m.monto), 0);
    const cuotaIglesiaJal = movsCuota.filter(m => m.modo_pago_cuota_iglesia == null).reduce((sum, m) => sum + Number(m.monto), 0);
    const cuotaIglesiaPA = movsCuota.filter(m => m.modo_pago_cuota_iglesia != null).reduce((sum, m) => sum + Number(m.monto), 0);

    const aportesExternos = ingresos
      .filter(m => m.origen_tipo === 'externo')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const actividadesEconomicas = ingresos
      .filter(m => m.origen_tipo === 'actividad')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const campanas = ingresos
      .filter(m => m.origen_tipo === 'campaña')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const egresosActividades = egresos
      .filter(m => m.origen_tipo === 'actividad')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const gastosGenerales = egresos
      .filter(m => m.categoria_egreso === 'general')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const gastosLiturgicos = egresos
      .filter(m => m.categoria_egreso === 'liturgico')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const gastosMisionales = egresos
      .filter(m => m.categoria_egreso === 'misional')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const egresosOtros = egresos
      .filter(m => !m.origen_tipo && !m.categoria_egreso)
      .reduce((sum, m) => sum + Number(m.monto), 0);

    return {
      ingresos: totalIngresosConBalance,
      egresos: totalEgresos,
      financiamiento,
      balance: totalIngresosConBalance + financiamiento - totalEgresos,
      cantidadMovimientos: movimientos?.length ?? 0,
      ingresosDesglosados: {
        aportesMensualesMiembros,
        cuotaIglesiaRecaudada,
        cuotaIglesiaJal,
        cuotaIglesiaPA,
        aportesExternos,
        actividadesEconomicas,
        campanas
      },
      egresosDesglosados: {
        actividades: egresosActividades,
        gastosGenerales,
        gastosLiturgicos,
        gastosMisionales,
        otros: egresosOtros
      }
    };
  },

  async getMovimientosByPeriodo(lumisialId: string, periodo: PeriodoBalance) {
    const { fechaInicio, fechaFin } = getPeriodoFechas(periodo);

    const { data, error } = await supabase
      .from('movimientos')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getCampanasResumen(lumisialId: string, periodo: PeriodoBalance) {
    const { fechaInicio, fechaFin } = getPeriodoFechas(periodo);

    const { data: campañas, error } = await supabase
      .from('campañas')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .lte('fecha_inicio', fechaFin)
      .gte('fecha_fin', fechaInicio);

    if (error) throw error;

    const campañasActivas = campañas?.filter(c => c.estado === 'en_curso') || [];
    const movimientos = await this.getMovimientosFondosCampana({ tipo: 'lumisial', id: lumisialId, nombre: '' }, periodo);
    const totalRecaudado = movimientos.filter(m => m.tipo === 'ingreso' && !m.envio_campana_id
      && !m.saldo_inicial_campana)
      .reduce((sum, m) => sum + Number(m.monto), 0);

    return {
      total: campañas?.length || 0,
      activas: campañasActivas.length,
      totalRecaudado
    };
  },

  async getEvolucionAnual(lumisialId: string, año: number): Promise<EvolucionMensualBalance[]> {
    const fechaInicio = `${año}-01-01`;
    const fechaFin = `${año}-12-31`;

    const { data: movimientos, error } = await supabase
      .from('movimientos')
      .select('fecha, tipo, monto, afecta_balance, origen_tipo')
      .eq('lumisial_id', lumisialId)
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) throw error;

    const porMes = new Map<number, MovimientoMonto[]>();
    // Ingresos y egresos filtran igual el dinero en tránsito: esta tabla vive en la misma pantalla
    // que el resumen del período, así que una asimetría entre ambos haría que la pantalla se
    // contradiga a sí misma mes a mes.
    for (const mov of filtrarQueAfectanBalance(movimientos || [])) {
      const mes = new Date(mov.fecha + 'T00:00:00').getMonth() + 1;
      porMes.set(mes, [...(porMes.get(mes) ?? []), mov]);
    }

    return Array.from({ length: 12 }, (_, i) => {
      const mes = i + 1;
      const { ingresos, egresos, financiamiento, balance } = calcularTotales(porMes.get(mes) ?? []);
      return { mes, ingresos, egresos, financiamiento, balance };
    });
  },

  async getActividadesResumen(lumisialId: string, periodo: PeriodoBalance) {
    const { fechaInicio, fechaFin } = getPeriodoFechas(periodo);

    const { data: actividades, error } = await supabase
      .from('actividades_economicas')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .lte('fecha_inicio', fechaFin)
      .gte('fecha_fin', fechaInicio);

    if (error) throw error;

    const actividadesActivas = actividades?.filter(a => a.estado === 'en_curso') || [];
    const utilidadesTotales = actividades?.reduce((sum, a) => sum + a.utilidad_real, 0) || 0;

    return {
      total: actividades?.length || 0,
      activas: actividadesActivas.length,
      utilidadesTotales
    };
  }
};
